import React, { useEffect, useRef, useState } from 'react';

import { sendTrackingPixelRequests } from '../../../../../analytics-library/sendTrackingPixelRequests';
import { triggerApstag } from '../../../../../sharedModules/apstag/integration';
import { ActionType } from '../../../../../sharedModules/getActionType';
import { LegacyActionEvent } from '../../../../../types/LegacyActionEvent';

import { getActions } from './getActions';
import type { HawkWidgetAdvancedProps } from './model';
import { getInitialState, reducer } from './reducer';

export const HawkWidgetAdvanced: React.FC<HawkWidgetAdvancedProps> = (props) => {
  const {
    data: defaultData,
    defaultTab,
    tabConfigs,
    switcherItems,
    selectedSwitcherItem: initialSelectedSwitcherItem,
    models: initialModels,
    params,
    getSortOptions,
    getDealData,
    dealData,
    translate,
    multiselectModels,
    sendAnalytics,
    genericSharedComponents: { HawkWidgetContextProvider },
    site,
    getWidgetResponse,
    postForWidgetResponse,
    getAiredaleFeeds,
    getModelSuggestions,
  } = props;

  const [state, dispatch] = React.useReducer(
    reducer,
    getInitialState({
      selectedSwitcherItem: initialSelectedSwitcherItem,
      switcherItems,
      activeTab: defaultTab,
      tabConfigs,
      params,
      getSortOptions,
      multiselectModels,
      defaultData,
      defaultModels: initialModels,
      dealData,
      getDealData,
    }),
  );

  // To ensure each tracking pixel URL is fetched only once per widget.
  // HawkWidgetAdvanced (due to bugs it contains) refetches deals also on 'Show less',
  // and when 'Show more' shows deals it has already shown before.
  const [trackingPixelRequestsSent, setTrackingPixelRequestsSent] = useState(
    state.dealData.deals.map((deal) => deal.tracking_pixel_url),
  );
  useEffect(() => {
    const newDeals = state.dealData.deals.filter(
      (deal) => !trackingPixelRequestsSent.includes(deal.tracking_pixel_url),
    );
    if (newDeals.length === 0) {
      return;
    }
    sendTrackingPixelRequests(newDeals);
    setTrackingPixelRequestsSent([
      ...trackingPixelRequestsSent,
      ...newDeals.map((deal) => deal.tracking_pixel_url),
    ]);
  }, [state.dealData.deals, trackingPixelRequestsSent]);

  const actions = getActions(dispatch);

  const activeTabRef = useRef(state.activeTab);

  const paramsRef = useRef(state.currentParams);
  const pageRef = useRef(state.currentPage);

  // Update data whenever the currentParams are changed
  useEffect(() => {
    // TODO(Petr): HAWK-7830 This runs 5 times when you click Show more in bundle widget
    const tabChanged =
      activeTabRef.current && activeTabRef.current.value !== state.activeTab?.value;
    const modelChanged = paramsRef?.current?.model_name !== state.currentParams?.model_name;
    const pageChanged = pageRef.current !== state.currentPage;

    // Ensure we update the model name if the tab was changed
    // We need to show the correct model data when we change the tabs after deselecting
    // and then selecting a model with different product types
    if (state.paramsChanged && state?.currentParams?.postcode !== '') {
      state.paramsChanged = false;
      state.sendPostRequest = true;
    }
    if (state.paramsChanged) {
      // Update the data if the params were changed and the tab weren't reset
      // or the current tab isn't the 'empty' tab
      const processEffect = async (): Promise<void> => {
        // No progressive filtering if the model/tab was changed or the filters were cleared
        const filtersCleared = state.gaData.actionType === ActionType.CLEAR_FILTERS;
        const progressiveFiltering = !modelChanged && !tabChanged && !filtersCleared;

        // Append deals if the offset is greater than 0 and the page wasn't changed
        const appendDeals = pageChanged ? false : (state.currentParams.offset || 0) > 0;
        const dealData = await actions.getData(
          getWidgetResponse,
          getAiredaleFeeds,
          {
            ...state.currentParams,
            ...(progressiveFiltering ? { progressive_filtering: 1 } : {}),
          },
          appendDeals,
          state,
          site,
        );

        if (tabChanged) {
          sendAnalytics({
            affiliate: {
              action: {
                type: ActionType.TAB_CHANGED,
                event: LegacyActionEvent.CLICK_FROM,
              },
              component: {
                label: state.activeTab?.label,
              },
            },
            dealData,
          });
        } else if (!state.gaData.skipGA) {
          sendAnalytics({
            affiliate: {
              action: {
                type: state.gaData.actionType,
                event: LegacyActionEvent.CLICK_FROM,
              },
              component: {
                label: state.gaData.label,
              },
            },
            dealData,
          });
        }

        triggerApstag();
      };
      processEffect();
    }

    if (state.sendPostRequest) {
      const processEffect = async (): Promise<void> => {
        const progressiveFiltering = !modelChanged && !tabChanged;

        const appendDeals = pageChanged ? false : (state.currentParams.offset ?? 0) > 0;
        const dealData = await actions.postData(
          postForWidgetResponse,
          getAiredaleFeeds,
          {
            ...state.currentParams,
            ...(progressiveFiltering ? { progressive_filtering: 1 } : {}),
          },
          appendDeals,
          state,
          site,
        );

        if (tabChanged) {
          sendAnalytics({
            affiliate: {
              action: {
                type: ActionType.TAB_CHANGED,
                event: LegacyActionEvent.CLICK_FROM,
              },
              component: {
                label: state.activeTab?.label,
              },
            },
            dealData,
          });
        } else if (!state.gaData.skipGA) {
          // TODO(Petr): HAWK-7830 This runs twice when you click Show more in bundle widget
          //  sending doubled "Load more" events.
          sendAnalytics({
            affiliate: {
              action: {
                type: state.gaData.actionType,
                event: LegacyActionEvent.CLICK_FROM,
              },
              component: {
                label: state.gaData.label,
              },
            },
            dealData,
          });
        }

        triggerApstag();
      };
      processEffect();
    }

    // Update the references so we don't consider the params or activeTab
    // to have changed when they haven't
    paramsRef.current = state.currentParams;
    activeTabRef.current = state.activeTab;
    pageRef.current = state.currentPage;
  }, [
    actions,
    getAiredaleFeeds,
    getWidgetResponse,
    postForWidgetResponse,
    sendAnalytics,
    site,
    state,
  ]);

  const memoizedProps = React.useMemo(() => {
    const uniqueModels = Array.from(new Set(state.dealData.deals.map((deal) => deal.model)));

    const data = {
      ...state.data[state.activeTab?.value],
      filters: (state.data[state.activeTab?.value]?.filters || []).concat([
        {
          filter_key: 'model_name',
          category: 'model',
          values: [],
          title:
            state.activeTab?.category === 'contracts'
              ? translate('modelFilterPhoneLabel')
              : translate('shopWindowCategoryFilter'),
        },
      ]),
    };

    return {
      ...props,
      data,
      dealData: state.dealData,
      tabs: tabConfigs,
      multiModel: uniqueModels.length > 1,
      activeTab: state.activeTab,
      params: state.currentParams,
      selectedFilters: state.currentSelectedFilters,
      loading: state.dataLoading,
      selectedSwitcherItem: state.selectedSwitcherItem,
      currentPage: state.currentPage,
      objectOfModels: state.objectOfModels,
      sortOptions: state.sortOptions,
      changePage: actions.changePage,
      loadMoreDeals: actions.loadMoreDeals,
      showFewerDeals: actions.showFewerDeals,
      changeTab: actions.changeTab,
      changeFilter: actions.changeFilter,
      changeRangeFilter: actions.changeRangeFilter,
      updateInputValue: actions.updateInputValue,
      updateInputPostcode: actions.updateInputPostcode,
      submitInput: actions.submitInput,
      submitPostcode: actions.submitPostcode,
      changeSort: actions.changeSort,
      clearFilters: actions.clearFilters,
      toggleCheckbox: actions.toggleCheckbox,
      changeCheckboxGroup: actions.changeCheckboxGroup,
      changeRadioButtonGroup: actions.changeRadioButtonGroup,
      getModelSuggestions,
      updateModelSuggestionsCallback: actions.updateModelSuggestions,
      scrollLeft: actions.scrollLeft,
      scrollRight: actions.scrollRight,
      loadParentModel: actions.loadParentModel,
      sortSimilarAtLastPosition: actions.sortSimilarAtLastPosition,
    };
  }, [
    actions.changeCheckboxGroup,
    actions.changeFilter,
    actions.changePage,
    actions.changeRadioButtonGroup,
    actions.changeRangeFilter,
    actions.changeSort,
    actions.changeTab,
    actions.clearFilters,
    actions.loadMoreDeals,
    actions.loadParentModel,
    actions.scrollLeft,
    actions.scrollRight,
    actions.showFewerDeals,
    actions.sortSimilarAtLastPosition,
    actions.submitInput,
    actions.submitPostcode,
    actions.toggleCheckbox,
    actions.updateInputPostcode,
    actions.updateInputValue,
    actions.updateModelSuggestions,
    getModelSuggestions,
    props,
    state.activeTab,
    state.currentPage,
    state.currentParams,
    state.currentSelectedFilters,
    state.data,
    state.dataLoading,
    state.dealData,
    state.objectOfModels,
    state.selectedSwitcherItem,
    state.sortOptions,
    tabConfigs,
    translate,
  ]);

  return <HawkWidgetContextProvider {...memoizedProps} />;
};
